// @atlas/contracts — a `auth` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
// ── Séma-építőkövek ──
export const LoginBodySchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
    /** „Emlékezz rám": true → perzisztens refresh-cookie (30 nap); false → session-cookie (böngésző-bezárásig). */
    rememberMe: z.boolean().optional(),
    /** Opcionális 2FA (TOTP vagy recovery) kód — ha az identity kétlépcsős. Write-only (SOHA nem logolt). */
    mfaCode: z.string().optional(),
});
/**
 * Login-hiba (401): a generikus üzeneten túl egy opcionális MFA-jelzés a frontendnek. `mfa_required` → a user
 * 2FA-s, kód-promptot kell mutatni; `mfa_invalid` → a beírt kód rossz. A jelzés a felhasználó-folyamat vezérléséhez
 * kell (a UI kód-mezőt nyit), nem szivárogtat jog-nevet/PII-t.
 */
export const LoginErrorSchema = z.object({
    message: z.string(),
    code: z.enum(['mfa_required', 'mfa_invalid']).optional(),
});
/** „Elfelejtett jelszó" — CSAK az e-mail-cím. A válasz mindig generikus (anti-enumeráció). */
export const ForgotPasswordBodySchema = z.object({
    // RFC 5321: a teljes cím max. 254 oktett — a plafon nélkül egy korlátlan string mehetne át a határon.
    email: z.string().email().max(254),
});
/**
 * Jelszó beállítása a levélből kapott linkkel.
 *
 * A `newPassword`-ön SZÁNDÉKOSAN csak KÜLSŐ korlát van (nem-üres + max. 200), a tartalmi jelszó-policy
 * (min. 12 karakter, kis- ÉS nagybetű ÉS szám, Unicode-tudatosan) NEM ismétlődik itt. MIÉRT: a policy
 * EGYETLEN autoritása az Odin (`validatePasswordPolicy`) — ha itt is leírnánk, két igazság lenne, és a
 * ts-rest Zod-validálás a szerver ELŐTT dobna 400-at, elvágva a 422-es, MEZŐHÖZ KÖTÖTT policy-hibákat
 * (`too_short`, `missing_uppercase`, `missing_digit`, …), amiket a UI a mező alatt ír ki. A UI a saját
 * (kényelmi) tükör-validációját a form-sémájában végzi, a szerver marad a döntőbíró.
 */
export const ResetPasswordBodySchema = z.object({
    /** A levélből kapott nyers token (az Odin 43 karakteres url-safe base64-et ad; a plafon védekező). */
    token: z.string().min(1).max(500),
    newPassword: z.string().min(1).max(200),
});
/**
 * „Elfelejtett jelszó" válasza — **azonos létező és nem létező fiókra**. A `message` szándékosan feltételes
 * fogalmazású („ha van fiók…"), hogy a UI-nak ne is legyen mit elárulnia.
 */
export const ForgotPasswordResponseSchema = z.object({
    accepted: z.literal(true),
    message: z.string(),
});
/** A bejelentkezett identity a JWKS-validált tokenből (walking skeleton — bővül a modulokkal). */
export const SessionSchema = z.object({
    sub: z.string(),
    scope: z.string(),
    clientId: z.string().nullable(),
    /** Az app-scope-olt feature-jogok (a sidebar/oldal-láthatósághoz). */
    permissions: z.array(z.string()),
    /** Kényelmi jelző: platform-admin (superuser) — a UI mindent mutat. */
    isPlatformAdmin: z.boolean(),
    /** OIDC profil-claimek (avatar-monogram, personalizáció) — a token claimjeiből. */
    email: z.string().nullable(),
    name: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /**
     * A saját BECENÉV (`identity_profiles.nickname`) — a megszólítás elsődleges forrása (`@atlas/shared`
     * `addressName`: becenév → különben keresztnév). A címtárból, best-effort: nincs / nem olvasható → null.
     */
    nickname: z.string().nullable(),
    /** A saját avatar presigned GET URL-je (SeaweedFS, S3-kompatibilis objektum-tár), ha van feltöltött profilkép — különben null (monogram). */
    avatarUrl: z.string().nullable(),
    /** A saját CSAPAT szín-tokenje (MORGENS-paletta) — az avatar-háttérhez (navbar); null → nincs csapat. */
    teamColor: z.string().nullable(),
    /**
     * AZ IDENTITY TÍPUSA — ez dönti el, MELYIK FELÜLET töltődik be (user-döntés: egy belépő-felület,
     * egy cím; a hitelesítés után a típus ágazik).
     *   • `internal` → a belső, admin felület,
     *   • `partner`  → a partner-portál.
     * A gépi (`service`) token ide sem jut el: a BFF `AuthGuard`-ja kizárja.
     */
    type: z.enum(['internal', 'partner']),
    /**
     * PARTNERNÉL a fiókonkénti portál-szerep térképe (`{ accountId: 'portal-admin'|'portal-member'|'portal-viewer' }`),
     * belsősnél üres objektum. A felület ebből tudja, mit ajánljon fel (a szerver ugyanezt érvényesíti); jog-DÖNTÉST
     * a kliens SOHA nem hoz belőle — csak megjelenítést.
     */
    portalAccounts: z.record(z.string(), z.string()),
    /**
     * MEGSZEMÉLYESÍTÉS (impersonation) — `null`, ha a hívó a SAJÁT nevében dolgozik.
     *
     * Ha ki van töltve, a token az RFC 8693 `act` claimjét hordozza: a `subject*` a MEGSZEMÉLYESÍTETT (akinek
     * a szemével a felület dolgozik — az összes többi mező is HOZZÁ tartozik: `sub`, `permissions`, `scope`),
     * az `actor*` pedig a VALÓDI aktor (a támogató munkatárs). A felület ebből rendereli a figyelmeztető
     * sávot, és ebből tudja, hogy a self-service (jelszó/2FA) műveletek most tiltottak.
     *
     * A forrás a TOKEN, nem kliens-állapot: így az adat akkor is helyes, ha a megszemélyesítő token lejárt és
     * a kliens csendben visszaállt a saját (aktor) tokenjére.
     */
    impersonation: z
        .object({
        subjectId: z.string(),
        subjectName: z.string().nullable(),
        subjectEmail: z.string().nullable(),
        actorId: z.string(),
        actorName: z.string().nullable(),
        /**
         * Az aktor NÉV-RÉSZEI — a NÉVSORREND a KLIENS döntése (magyarul vezetéknév elöl, más nyelven
         * keresztnév elöl), ezért a szerver nem fűz össze nevet, hanem részeket ad (`formatName`).
         *
         * MIÉRT KELL (mért hiba): a token `act.name`-je a `display_name`, az pedig a migrált adatban
         * „keresztnév vezetéknév" alakú — így a sávban magyar felületen is „Szilárd Reizner" jelent meg
         * („Reizner Szilárd" helyett), miközben ugyanaz a személy a listákon helyes sorrendben állt.
         * A megszemélyesített félnek ugyanez a két mezője a válasz GYÖKERÉBEN van (`givenName`/`familyName`),
         * mert a token `sub`-ja megszemélyesítés közben MAGA a cél.
         *
         * `null` marad, ha a címtár nem elérhető (gépi kredenciál nélkül) — a felület ilyenkor az
         * `actorName`-re esik vissza: kevésbé szép sorrend, de sosem üres sáv.
         */
        actorGivenName: z.string().nullable(),
        actorFamilyName: z.string().nullable(),
    })
        .nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// MEGSZEMÉLYESÍTÉS (impersonation) — „belépés más nevében", a támogatói folyamat Atlas-oldali szerződése.
//
// A PLATFORM adja a tokent (`POST /iam/impersonate`), a jog-kapu EGY kulcs: a hívó SAJÁT tokenjének
// `iam:impersonate` scope-ja — a jogosultság-konzol „Megszemélyesítés" pipája (`docs/foundations/auth.md` §11).
// A böngésző ITT kapja meg a megszemélyesítő access-tokent: attól kezdve minden kérése a cél user
// jogképével megy, amíg a token le nem jár, vagy amíg a felhasználó vissza nem lép (`/impersonation/stop`
// + a SAJÁT refresh-cookie-ja).
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export const ImpersonationStartBodySchema = z.object({
    /** A megszemélyesítendő identity id-ja (munkatárs vagy kapcsolattartó). */
    identityId: z.string().uuid(),
    /**
     * Opcionális, szabad-szöveges ok — az AUDIT-ba kerül (Odin `iam.impersonate.start`, `after.reason`).
     * A felület nem kényszeríti ki: egy kötelező, de kitalált indok rosszabb, mint a semmi.
     */
    reason: z.string().trim().max(200).optional(),
});
export const ImpersonationStartResponseSchema = z.object({
    /**
     * A MEGSZEMÉLYESÍTŐ access-token (rövid életű, NEM újítható meg). A böngésző memóriájába kerül, mint a
     * hétköznapi access-token; a refresh-cookie VÁLTOZATLANUL az aktoré marad — ez a „visszalépés" útja.
     */
    accessToken: z.string(),
    /** Meddig él (másodperc) — a felület ebből számol visszajelzést/lejáratot. */
    expiresIn: z.number().int().positive(),
    /** Kit személyesítünk meg (a sáv feliratához). */
    subject: z.object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().nullable(),
        type: z.enum(['internal', 'partner']),
    }),
});
export const ImpersonationStopResponseSchema = z.object({
    /**
     * Az AKTOR friss access-tokenje (a saját refresh-cookie-jából rotálva) — így a „vissza" EGY kérés, és a
     * felület azonnal a valódi identitással dolgozik tovább. `null`, ha nem sikerült visszaadni.
     */
    accessToken: z.string().nullable(),
    /**
     * MIÉRT nincs token — a `refreshSession()` HÁROMÁLLAPOTÚ mintája (`lib/auth.ts`), mert a két ok más
     * felhasználói választ kíván:
     *   • `expired`   — az AKTOR munkamenete megszűnt (nincs refresh-cookie, vagy az Odin 401-et adott):
     *                   a felületnek KI KELL léptetnie, mert a megszemélyesítő tokent nem szabad megtartani;
     *   • `transient` — átmeneti upstream-hiba (az Odin nem elérhető): a refresh-cookie ÉL, a kiléptetés
     *                   adatvesztés lenne, és a kézi újra-belépés az Odin login-limitjébe (5/15 perc) futna.
     *
     * Sikeres tokennél hiányzik. Enélkül a felület a két esetet nem tudta megkülönböztetni, és a lejárt
     * aktor-munkamenetnél BENT RAGADT a megszemélyesítésben (mért hiba).
     */
    reason: z.enum(['expired', 'transient']).optional(),
});
//# sourceMappingURL=schemas.js.map